/**
 * Onboarding Input Component
 * Dynamic input component that renders different UI based on onboarding step
 */

import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput } from 'react-native';
import Button from '../common/Button';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import type { OnboardingStepData, OnboardingOption, DiagnosticData } from '../../api/types';
import DiagnosticCard from './DiagnosticCard';

interface OnboardingInputProps {
  step: string;
  inputType?: string;
  options?: OnboardingOption[];
  buttonLabel?: string;
  diagnosticData?: DiagnosticData | null;
  onSubmit: (data: OnboardingStepData) => void;
  onDiagnosticAction?: (action: string) => void;
  isLoading?: boolean;
}

export default function OnboardingInput({
  step,
  inputType,
  options,
  buttonLabel,
  diagnosticData,
  onSubmit,
  onDiagnosticAction,
  isLoading = false,
}: OnboardingInputProps) {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [textInputValue, setTextInputValue] = useState<string>('');

  // Reset state when step changes
  useEffect(() => {
    setSelectedOption('');
    setTextInputValue('');
  }, [step]);

  const handleSubmit = () => {
    switch (step) {
      case 'welcome':
        onSubmit({});
        break;
      default:
        // Generic handler for all radio questions
        onSubmit({
          value: selectedOption,
          text_input: textInputValue || undefined,
        });
        break;
    }
  };

  // Find if the selected option has text input
  const selectedOptionData = options?.find((o) => o.value === selectedOption);
  const showTextInput = selectedOptionData?.has_text_input === true;

  switch (inputType) {
    case 'button':
      return (
        <View style={styles.container}>
          <Button
            title={buttonLabel || 'Continuer'}
            onPress={handleSubmit}
            loading={isLoading}
            fullWidth
          />
        </View>
      );

    case 'radio':
      return (
        <View style={styles.container}>
          <ScrollView style={styles.radioContainer}>
            {options?.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.radioOption,
                  selectedOption === option.value && styles.radioOptionSelected,
                ]}
                onPress={() => {
                  setSelectedOption(option.value);
                  // Reset text input when changing options
                  if (option.value !== selectedOption) {
                    setTextInputValue('');
                  }
                }}
              >
                <View style={styles.radioContent}>
                  {option.emoji && (
                    <Text style={styles.radioEmoji}>{option.emoji}</Text>
                  )}
                  <View style={styles.radioTextContainer}>
                    <Text
                      style={[
                        styles.radioLabel,
                        selectedOption === option.value && styles.radioLabelSelected,
                      ]}
                    >
                      {option.label}
                    </Text>
                    {option.description && (
                      <Text style={styles.radioDescription}>
                        {option.description}
                      </Text>
                    )}
                  </View>
                </View>
                {/* Show text input when option with has_text_input is selected */}
                {option.has_text_input && selectedOption === option.value && (
                  <View style={styles.textInputContainer}>
                    <TextInput
                      style={styles.textInput}
                      placeholder={option.text_input_placeholder || ''}
                      placeholderTextColor={colors.textTertiary}
                      value={textInputValue}
                      onChangeText={setTextInputValue}
                      autoFocus
                    />
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
          <Button
            title="Suivant"
            onPress={handleSubmit}
            loading={isLoading}
            fullWidth
            disabled={!selectedOption}
          />
        </View>
      );

    case 'diagnostic':
      if (diagnosticData && onDiagnosticAction) {
        return (
          <DiagnosticCard
            data={diagnosticData}
            onAction={onDiagnosticAction}
            isLoading={isLoading}
          />
        );
      }
      return null;

    default:
      return (
        <View style={styles.container}>
          <Button
            title="Continuer"
            onPress={handleSubmit}
            loading={isLoading}
            fullWidth
          />
        </View>
      );
  }
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.lg,
  },
  radioContainer: {
    maxHeight: 500,
    marginBottom: spacing.md,
  },
  radioOption: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.sm,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background,
  },
  radioOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  radioContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  radioEmoji: {
    fontSize: 24,
    marginRight: spacing.md,
    marginTop: 2,
  },
  radioTextContainer: {
    flex: 1,
  },
  radioLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
  },
  radioLabelSelected: {
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary,
  },
  radioDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
    lineHeight: typography.fontSize.sm * 1.4,
  },
  textInputContainer: {
    marginTop: spacing.sm,
    marginLeft: 40,
  },
  textInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.md,
    color: colors.text,
    backgroundColor: colors.background,
  },
});
